import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import type { CreateEventRequest } from "@/services/api/events";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ShareEventModal } from "@/components/customer/share/ShareEventModal";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UserHeader } from "@/components/customer/UserHeader";
import { USER_ROUTES, ADMIN_ROUTES } from "@/routes";
import { useAuth } from "@/hooks/useAuth";
import { EventCard } from "@/components/customer/events/EventCard";
import { CreateEventForm } from "@/components/customer/events/CreateEventForm";
import { EventTypeModal } from "@/components/customer/events/EventTypeModal";
import { EmptyEventsState } from "@/components/customer/events/EmptyEventsState";
import type { EventTypeChoice } from "@/lib/event-templates";
import { getEventStats } from "@/utils/eventUtils";
import { useEventsStore } from "@/stores/eventsStore";
import { useStripeAccountStore } from "@/stores/stripeAccountStore";
import { fetchStripeAccountStatus } from "@/services/api/stripeConnect";
import { DeleteEventDialog } from "@/components/customer/events/DeleteEventDialog";
import { TrashSection } from "@/components/customer/events/TrashSection";
import { ArchivedEventsSection } from "@/components/customer/events/ArchivedEventsSection";
import { setEventArchived } from "@/services/api/events";
import { forgetActiveEvent } from "@/utils/active-event";
import { PendingPlanBanner } from "@/components/customer/purchases/PendingPlanBanner";
import { usePlanSelectionStore, type PlanType } from "@/stores/planSelectionStore";
import { FooterLinkColumns } from "@/components/common/FooterLinkColumns";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const _locale = useRouteLocale();

  usePageMeta({
    title: t("dashboard.metaTitle"),
    description: t("dashboard.metaTitle"),
    locale: _locale,
    path: USER_ROUTES.events(_locale),
    noindex: true,
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentLocale = _locale || 'ro';
  const stripeStatus = useStripeAccountStore((state) => state.status);
  const setStripeStatus = useStripeAccountStore((state) => state.setStatus);

  // JUB-36: the event type modal is the first step of creation; it only
  // gates whether/how the create form opens, so it stays local to this page
  // rather than living in the shared events store.
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [pendingEventType, setPendingEventType] = useState<EventTypeChoice>("wedding");
  // JUB-157: Trash/archive lists refresh when the dashboard changes something.
  const [lifecycleKey, setLifecycleKey] = useState(0);
  // JUB-157: after registering with a paid plan, the account creates its event
  // first and then pays for that event (one payment = one event).
  const [searchParams, setSearchParams] = useSearchParams();
  const buyPlanParam = searchParams.get('buyPlan');
  const pendingPlan: PlanType | null = buyPlanParam === 'standard' || buyPlanParam === 'premium' ? buyPlanParam : null;
  const clearSelectedPlan = usePlanSelectionStore((state) => state.clearSelectedPlan);
  // JUB-154: with the onboarding_wizard flag on, "create" opens the wizard
  // (the paid plan picked at registration rides along); off keeps the modal.
  const { isEnabled } = useFeatureFlags();
  const openCreate = () => {
    if (isEnabled('onboarding_wizard')) {
      navigate(`${USER_ROUTES.onboarding(currentLocale)}${pendingPlan ? `?buyPlan=${pendingPlan}` : ''}`);
      return;
    }
    setShowTypeModal(true);
  };

  // Redirect administrators to management events listing
  useEffect(() => {
    if (user?.user_admin) {
      navigate(ADMIN_ROUTES.events(currentLocale), { replace: true });
    }
  }, [user, navigate, currentLocale]);

  // Zustand store selectors - optimized to prevent unnecessary re-renders
  const events = useEventsStore((state) => state.events);
  const loading = useEventsStore((state) => state.loading);
  const loadError = useEventsStore((state) => state.loadError);
  const creating = useEventsStore((state) => state.creating);
  const deleting = useEventsStore((state) => state.deleting);
  const shareEvent = useEventsStore((state) => state.shareEvent);
  const deleteEventId = useEventsStore((state) => state.deleteEventId);
  const showCreateForm = useEventsStore((state) => state.showCreateForm);
  
  // Zustand actions
  const loadEvents = useEventsStore((state) => state.loadEvents);
  const createEventAction = useEventsStore((state) => state.createEvent);
  const deleteEventAction = useEventsStore((state) => state.deleteEvent);
  const setShareEvent = useEventsStore((state) => state.setShareEvent);
  const setDeleteEventId = useEventsStore((state) => state.setDeleteEventId);
  const setShowCreateForm = useEventsStore((state) => state.setShowCreateForm);
  const clearShareEvent = useEventsStore((state) => state.clearShareEvent);
  const clearDeleteEventId = useEventsStore((state) => state.clearDeleteEventId);

  useEffect(() => {
    loadEvents().catch((error: unknown) => {
      console.error('Failed to load events:', error);
      toast.error(t('common.error'));
    });
  }, [loadEvents, t]);

  // Always fetch Stripe status when Dashboard mounts so the "Bancă conectată" badge
  // reflects the current API state (e.g. after connecting, refresh or new tab shows correct status).
  useEffect(() => {
    if (!user?.uuid) {
      setStripeStatus(null);
      return undefined;
    }

    let isMounted = true;
    const fetchStripeStatus = async () => {
      try {
        const status = await fetchStripeAccountStatus();
        if (isMounted) {
          setStripeStatus(status);
        }
      } catch (error) {
        console.error('Failed to load Stripe status:', error);
      }
    };

    fetchStripeStatus();
    return () => {
      isMounted = false;
    };
  }, [user?.uuid, setStripeStatus]);

  // Note: Events are now unlimited - each event has its own plan
  // Plan limits (guests, gifts) are now per-event, not per-user

  const handleCreateEvent = async (eventData: CreateEventRequest) => {
    try {
      const newEvent = await createEventAction(eventData);
      
      if (!newEvent?.uuid) {
        console.error('Event created but no UUID in response:', newEvent);
        toast.error(t('events.created') + ' but failed to navigate');
        await loadEvents();
        return;
      }
      
      toast.success(t('events.created'));
      if (pendingPlan) {
        // Continue the registration purchase for the event just created.
        const params = new URLSearchParams({ plan: pendingPlan, event: newEvent.uuid });
        navigate(`/${currentLocale}/payment?${params.toString()}`);
        return;
      }
      // Navigate to event management page using UUID
      navigate(USER_ROUTES.event(currentLocale, newEvent.uuid));
    } catch (error: unknown) {
      console.error('Failed to create event:', error);
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || t('common.error');
      toast.error(errorMessage);
    }
  };

  const handleDeleteEvent = async (eventUuid: string) => {
    try {
      await deleteEventAction(eventUuid);
      if (user?.uuid) forgetActiveEvent(user.uuid, eventUuid);
      setLifecycleKey((k) => k + 1);
      toast.success(t('events.deleteDialog.success'));
    } catch (error: unknown) {
      console.error('Failed to delete event:', error);
      toast.error(t('events.deleteDialog.error'));
    }
  };

  const handleArchiveEvent = async (eventUuid: string) => {
    try {
      await setEventArchived(eventUuid, true);
      setLifecycleKey((k) => k + 1);
      toast.success(t('events.archive.archived'));
      await loadEvents();
    } catch {
      toast.error(t('events.archive.error'));
    }
  };

  const reloadAfterLifecycleChange = () => {
    setLifecycleKey((k) => k + 1);
    loadEvents().catch(() => undefined);
  };

  const eventPendingDelete = events.find((event) => event.uuid === deleteEventId) ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      {/* Header */}
      <UserHeader currentLocale={currentLocale} active="events" />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)]">
        <div className="mb-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-2xl font-bold">
            {t('events.list')}
          </h2>
          <Button
            onClick={() => {
              // Note: Events are now unlimited - each event has its own plan
              // JUB-36: the type modal is the first step of creation; closing
              // an already-open form just collapses it, same as before.
              if (showCreateForm) {
                setShowCreateForm(false);
              } else {
                openCreate();
              }
            }}
            className="inline-flex items-center gap-2 whitespace-nowrap bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
            data-testid="dashboard-create-event-button"
          >
            <Plus className="h-4 w-4 shrink-0" />
            {t('events.create')}
          </Button>
        </div>

        {pendingPlan && (
          <PendingPlanBanner
            plan={pendingPlan}
            onCreateEvent={openCreate}
            onDismiss={() => {
              clearSelectedPlan();
              const next = new URLSearchParams(searchParams);
              next.delete('buyPlan');
              setSearchParams(next, { replace: true });
            }}
          />
        )}

        {/* JUB-36: event type selection modal - first step of creation */}
        <EventTypeModal
          open={showTypeModal}
          onOpenChange={setShowTypeModal}
          onSelect={(choice) => {
            setPendingEventType(choice);
            setShowCreateForm(true);
          }}
        />

        {/* Create Event Form */}
        {showCreateForm && (
          <CreateEventForm
            onSubmit={handleCreateEvent}
            onCancel={() => {
              setShowCreateForm(false);
            }}
            isCreating={creating}
            initialEventType={pendingEventType}
          />
        )}

        {/* Events List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        ) : loadError && events.length === 0 ? (
          // JUB-157 D2: a failed load is not an empty account — never offer
          // "create your first event" here, it invites a duplicate event.
          <div
            className="mx-auto max-w-md rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-6 text-center text-sm"
            role="alert"
            data-testid="events-load-error"
          >
            <p>{t('events.loadError')}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => { loadEvents().catch(() => undefined); }}
              data-testid="events-load-retry"
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : events.length === 0 ? (
          <EmptyEventsState
            onCreateEvent={() => {
              // Note: Events are now unlimited - each event has its own plan
              openCreate();
            }}
            currentLocale={currentLocale}
          />
        ) : (
          <div className={cn(
            "grid gap-6",
            events.length === 1 
              ? "grid-cols-1 max-w-2xl" 
              : "grid-cols-1 md:grid-cols-2"
          )} data-testid="event-list">
            {events.map((event) => {
              const planCode = (event?.plan?.toLowerCase() || 'free') as
                | 'free'
                | 'standard'
                | 'premium';
              const hasStripeAccount = Boolean(
                stripeStatus?.stripe_account_id || stripeStatus?.connected
              );
              const bankLinkStatus = !hasStripeAccount
                ? 'not_linked'
                : stripeStatus?.charges_enabled && stripeStatus?.payouts_enabled
                  ? 'linked'
                  : stripeStatus?.onboarding_state === 'pending_verification'
                    ? 'pending'
                    : 'action_required';
              const stats = getEventStats(event, {
                plan: planCode,
                bankLinkStatus,
                stripeAccountConnected: Boolean(stripeStatus?.stripe_account_id),
              });

              return (
                <EventCard
                  key={event.uuid}
                  event={event}
                  stats={stats}
                  currentLocale={currentLocale}
                  onShare={setShareEvent}
                  currentEventsCount={events.length}
                  onDelete={setDeleteEventId}
                  onArchive={handleArchiveEvent}
                />
              );
            })}
          </div>
        )}

        {/* Share Event Modal */}
        <ShareEventModal
          open={!!shareEvent}
          onOpenChange={(open) => !open && clearShareEvent()}
          event={shareEvent}
          onPublished={(updatedEvent) => {
            setShareEvent(updatedEvent);
            loadEvents().catch((error: unknown) => {
              console.error('Failed to refresh events after publish:', error);
            });
          }}
        />

        {/* JUB-157: archived events and the Trash */}
        <ArchivedEventsSection
          currentLocale={currentLocale}
          refreshKey={lifecycleKey}
          onUnarchived={reloadAfterLifecycleChange}
        />
        <TrashSection
          currentLocale={currentLocale}
          refreshKey={lifecycleKey}
          onRestored={reloadAfterLifecycleChange}
        />

        {/* Delete = move to Trash for 30 days (JUB-157) */}
        <DeleteEventDialog
          open={!!deleteEventId}
          isPaid={(eventPendingDelete?.plan ?? 'free') !== 'free'}
          busy={deleting}
          onConfirm={() => deleteEventId && handleDeleteEvent(deleteEventId)}
          onOpenChange={(open) => !open && clearDeleteEventId()}
        />
      </main>

      {/* Footer */}
      <footer className="border-t" style={{ backgroundImage: 'linear-gradient(to right bottom, rgb(255, 250, 250), rgba(249, 236, 238, 0.2))', borderTopColor: 'rgba(237, 222, 227, 0.5)' }}>
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Logo & Mission */}
            <div>
              <h3 className="font-logo text-2xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent mb-2">
                {t('header.logo')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('footer.mission')}
              </p>
            </div>

            <FooterLinkColumns />
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                {t('footer.copyright', { year: String(new Date().getFullYear()) })}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
